import {Draft, isDraft, original} from 'immer';
import cloneDeep from 'lodash/cloneDeep';
import merge from 'lodash/merge';

import {Metric} from 'app_api/projects/metrics/types';
import {BehaviorEntity as Behavior} from 'app_api/projects/behaviors/types';
import {EventItem} from 'common/query-builder/query-block/events-menu/types';
import {
  isCohortMenuSectionItem,
  isEventMenuSectionItem,
  isFormulaMenuSectionItem,
  isFunnelMenuSectionItem,
  isProfileMenuSectionItem,
  isRetentionMenuSectionItem,
  isSavedMetricMenuSectionItem,
  isSimpleMenuSectionItem,
  MultiMetricsSectionItem,
} from 'common/query-builder/query-block/multi-metrics-menu/types';
import {isBehaviorShowClause, isFormulaShowClause} from 'common/report/insights/models/utils';
import * as BookmarkTypes from 'common/types/reports/bookmark';
import {updateOrAddAtIndex} from 'common/util/array';
import {isSavedCohort, isScdProperty} from 'common/query-builder/query-block/properties-menu/types';
import {PropertyType} from 'common/report/constants';
import {insightsFilterAttrsFromPropertyFilter} from 'common/widgets/property-filter-menu/models/insights-filter';
import {convertPropertySelectiontoBookmarkProperty} from 'common/query-builder/query-entry-section/row-types/metric-entry/metrics/metric-entry-util';

import {FILTER_DETERMINER_ALL} from '../util/constants';
import {ShowClause} from '../util/types';
import {DEFAULT_CLAUSE_BY_TYPE} from './constants';
import {getSanitizedClause, isModifiedMetric} from './utils/bookmark';

/** these types allow passing a MetricType and dynamically type it to the corresponding show clause tye */
type MetricType = BookmarkTypes.TopLevelMetricType.Metric | BookmarkTypes.TopLevelMetricType.Formula;
type MetricTypeMap<T> = T extends BookmarkTypes.TopLevelMetricType.Metric
  ? BookmarkTypes.BehaviorShowClause
  : T extends BookmarkTypes.TopLevelMetricType.Formula
    ? BookmarkTypes.FormulaShowClause
    : never;

/**
 * Returns a top level metric or a metric referenced inside a formula
 */
export function getMetricAtIndex<T extends MetricType>({
  index,
  parentMetricIndex,
  params,
  type,
}: {
  index: number;
  parentMetricIndex?: number;
  params: Draft<BookmarkTypes.InsightsBookmarkParams> | BookmarkTypes.InsightsBookmarkParams;
  type?: T;
}): MetricTypeMap<T> | null {
  const clause = params?.sections.show[parentMetricIndex ?? index];

  if (!clause) {
    return null;
  }

  let resultClause;
  if (parentMetricIndex !== undefined) {
    // validates against nested formulas
    if (!isFormulaShowClause(clause)) {
      return null;
    }

    if (index === -1) {
      return clause as MetricTypeMap<T>;
    }

    resultClause = clause.referencedMetrics![index];
  } else {
    resultClause = clause;
  }

  resultClause = resultClause as MetricTypeMap<T>;

  if (type === BookmarkTypes.TopLevelMetricType.Metric && !isBehaviorShowClause(resultClause)) {
    throw new Error(`Metric action not supported.`);
  } else if (type === BookmarkTypes.TopLevelMetricType.Formula && !isFormulaShowClause(resultClause)) {
    throw new Error(`Formula action not supported.`);
  }

  return resultClause;
}

/**
 * Applies a new clause selection to a top level metric or a referenced metric inside a formula
 */
export function updateMetricOrReferencedMetric({
  index,
  parentMetricIndex,
  params,
  newClause,
  keepOverrides,
}: {
  index: number;
  parentMetricIndex?: number;
  params: Draft<BookmarkTypes.InsightsBookmarkParams>;
  newClause: ShowClause;
  /** when true, any override fields in the clause will be merged with the resulting update */
  keepOverrides?: boolean;
}) {
  if (parentMetricIndex !== undefined) {
    const clause = params.sections.show[parentMetricIndex];

    // validates against nested formulas
    if (!isFormulaShowClause(clause) || newClause.type !== BookmarkTypes.TopLevelMetricType.Metric) {
      throw Error(`Unsupported action for formula`);
    }

    const referencedClause = clause.referencedMetrics?.[index];
    const overrides = keepOverrides && referencedClause ? getSanitizedClause(referencedClause).overrides ?? {} : {};

    // eslint-disable-next-line prototype-pollution-security-rules/detect-merge-objects
    newClause = merge(newClause, overrides);

    // Flag a diverged saved operand (and its formula) so serialization keeps its full definition.
    // Drag-and-drop mutates the operand in place, so diff against the immer original, not the draft.
    const previousReferencedClause = isDraft(referencedClause)
      ? original(referencedClause) ?? referencedClause
      : referencedClause;
    const isModified = isModifiedMetric(newClause, previousReferencedClause);
    if (isModified && newClause.id) {
      newClause = {...newClause, hasUnsavedChanges: true};
    }

    // update the parent formula metric
    params.sections.show[parentMetricIndex] = {
      ...clause,
      referencedMetrics: updateOrAddAtIndex(clause.referencedMetrics ?? [], index, newClause),
      ...(isModified && clause.id ? {hasUnsavedChanges: true} : {}),
    };
  } else {
    const overrides = keepOverrides ? getSanitizedClause(params.sections.show?.[index]).overrides ?? {} : {};

    // eslint-disable-next-line prototype-pollution-security-rules/detect-merge-objects
    newClause = merge(newClause, overrides);

    params.sections.show = updateOrAddAtIndex(params.sections.show ?? [], index, newClause);
  }
}

/**
 * Converts raw event behaviors into simple behaviors
 *
 * This is needed as we roll out First Class Metrics and formulas in appdb are migrated
 */
export function maybeConvertClauseToSimpleBehavior(clause: BookmarkTypes.BehaviorShowClause) {
  const behavior = clause.behavior;
  const behaviorType = behavior?.type;
  if (
    !behaviorType ||
    (behaviorType !== BookmarkTypes.MetricType.Event && behaviorType !== BookmarkTypes.MetricType.CustomEvent)
  ) {
    return clause;
  }

  clause.behavior = {
    type: BookmarkTypes.MetricType.Simple,
    name: ``,
    hasUnsavedChanges: false,
    resourceType: BookmarkTypes.InsightsResourceType.Events,
    dataGroupId: null,
    filters: [],
    behaviors: [
      {
        type: behaviorType,
        id: behavior.id,
        name: behavior.name,
        filters: behavior.filters,
        filtersDeterminer: behavior.filtersDeterminer || `all`,
      },
    ],
  };

  return clause;
}

/**
 * Creates a new show clause based on a metric model selection
 */
export function getClauseFromMetricModel(
  metric: Metric,
  opts?: {convertEventsToSimpleBehaviors?: boolean | undefined; overrides?: Obj | undefined},
): ShowClause {
  let clause: BookmarkTypes.ShowClause;

  metric = cloneDeep(metric);

  const commonAttributes = {
    id: metric.id,
    name: metric.name,
    display: metric.definition.display ?? {},
    measurement: metric.definition.measurement ?? {},
  };

  // handles saved formula selection
  if (metric.type === BookmarkTypes.TopLevelMetricType.Formula) {
    clause = {
      type: BookmarkTypes.TopLevelMetricType.Formula,
      goals: metric.definition.goals,
      ...commonAttributes,
      ...metric.definition.formula,
    } satisfies BookmarkTypes.FormulaShowClause;

    if (opts?.convertEventsToSimpleBehaviors) {
      clause.referencedMetrics = clause.referencedMetrics?.map(maybeConvertClauseToSimpleBehavior);
    }
  } else {
    clause = {
      type: BookmarkTypes.TopLevelMetricType.Metric,
      ...commonAttributes,
      ...metric.definition,
    } satisfies BookmarkTypes.BehaviorShowClause;
  }

  if (opts?.overrides) {
    // eslint-disable-next-line prototype-pollution-security-rules/detect-merge-objects
    clause = merge(clause, opts.overrides);
  }

  return clause;
}

/**
 * Creates a new show clause based on a saved behavior  model selection
 */
export function getClauseFromBehaviorModel({
  behavior,
  clause,
}: {
  behavior: Behavior;
  clause?: BookmarkTypes.BehaviorShowClause | null;
}): BookmarkTypes.BehaviorShowClause {
  if (clause && clause.behavior!.type !== behavior.type) {
    throw new Error(`Attempted to select a different behavior type`);
  }

  const behaviorFromModel = {
    type: behavior.type as BookmarkTypes.MetricType,
    name: behavior.name,
    id: behavior.id,
    ...cloneDeep(behavior.definition.behavior),
  };

  // gets the default measurement for each behavior type
  const defaultMeasurement: BookmarkTypes.BehaviorMeasurement = DEFAULT_CLAUSE_BY_TYPE[behavior.type]?.measurement
    ? cloneDeep(DEFAULT_CLAUSE_BY_TYPE[behavior.type]!.measurement!)
    : {
        math: BookmarkTypes.PRIMITIVE_MATH_TYPES.UNIQUE,
      };

  /**
   * @todo(metrics) perform clause cleanup and validation for the measurement field
   * For example: if the selected behavior changes from 3 to 2 steps, the measurement for "Conversion Step 2-3" becomes invalid
   */

  return {
    type: BookmarkTypes.TopLevelMetricType.Metric,
    measurement: clause?.measurement ?? defaultMeasurement,
    ...clause,
    behavior: behaviorFromModel,
  };
}

/**
 * Returns a deep clone of the default show clause for the specified metric type.
 *
 * @param type - The metric type to get the default show clause for
 * @returns A deep clone of the default show clause if the type exists in the defaults, otherwise null
 */
export function getEmptyShowClauseForType(type: BookmarkTypes.MetricType | null) {
  if (!type || type in DEFAULT_CLAUSE_BY_TYPE === false) {
    return null;
  }

  return cloneDeep(DEFAULT_CLAUSE_BY_TYPE[type]!);
}

/**
 * Returns a new ShowClause based on a selected metrics menu item
 */
export function getShowClauseFromMetricItemSelection({
  dataGroupId,
  selectedItem,
}: {
  dataGroupId: string | null;
  selectedItem: MultiMetricsSectionItem;
}): ShowClause {
  let clause: ShowClause;

  /** a raw event selection results in a new metric with a new simple behavior */
  if (isEventMenuSectionItem(selectedItem) || selectedItem.itemFromInput) {
    const selectedEvent = selectedItem.value as EventItem;
    clause = {
      type: BookmarkTypes.TopLevelMetricType.Metric,
      measurement: cloneDeep(DEFAULT_CLAUSE_BY_TYPE[BookmarkTypes.MetricType.Simple]!.measurement),
      display: {},
      behavior: {
        type: BookmarkTypes.MetricType.Simple,
        name: undefined,
        resourceType: BookmarkTypes.InsightsResourceType.Events,
        filters: [],
        filtersDeterminer: FILTER_DETERMINER_ALL,
        behaviors: [
          {
            type: selectedEvent.custom ? BookmarkTypes.MetricType.CustomEvent : BookmarkTypes.MetricType.Event,
            id: selectedEvent.id ?? undefined,
            name: selectedEvent.value,
            filters: [],
            filtersDeterminer: FILTER_DETERMINER_ALL,
          },
        ],
      },
    } satisfies BookmarkTypes.BehaviorShowClause;
  } else if (
    isSimpleMenuSectionItem(selectedItem) ||
    isFunnelMenuSectionItem(selectedItem) ||
    isRetentionMenuSectionItem(selectedItem)
  ) {
    /** menu selections like saved funnel, retention, or simple behaviors will result in a new metric with a saved behavior */
    const selectedBehaviorModel = selectedItem.value!;
    clause = getClauseFromBehaviorModel({behavior: selectedBehaviorModel});
  } else if (isFormulaMenuSectionItem(selectedItem) || isSavedMetricMenuSectionItem(selectedItem)) {
    /** handles saved formula or metric selection */
    clause = getClauseFromMetricModel(selectedItem.value!, {convertEventsToSimpleBehaviors: true});
  } else if (isCohortMenuSectionItem(selectedItem)) {
    /** handles cohort selection */
    clause = cloneDeep(DEFAULT_CLAUSE_BY_TYPE[BookmarkTypes.MetricType.Cohort]!) as BookmarkTypes.BehaviorShowClause;
    if (isSavedCohort(selectedItem.value!)) {
      clause.behavior!.id = selectedItem.value.value;
      clause.behavior!.dataGroupId = selectedItem.value.dataGroupId;
    } else {
      clause.behavior!.name = selectedItem.value!.value.cohort.name;
      clause.behavior!.raw_cohort = selectedItem.value!.value.cohort.raw_cohort;
    }
  } else if (isProfileMenuSectionItem(selectedItem)) {
    /** handles profile and profile property selection */
    const itemValue = selectedItem.value;
    clause = cloneDeep(DEFAULT_CLAUSE_BY_TYPE[BookmarkTypes.MetricType.People]!) as BookmarkTypes.BehaviorShowClause;
    /** handle selecting SCD properties for profile metrics */
    if (itemValue && itemValue !== `$all_people` && isScdProperty(itemValue)) {
      if (itemValue.type === PropertyType.Number) {
        /** create a numeric aggregation when selecting an SCD property from the metrics menu  */
        clause.measurement!.property = convertPropertySelectiontoBookmarkProperty(itemValue);
      } else {
        /** adds a "as of time of event" filter when selecting a string SCD property from the metrics menu */
        clause.behavior!.filters = [
          insightsFilterAttrsFromPropertyFilter({
            ...(itemValue.customProperty ? {customProperty: itemValue.customProperty} : {}),
            ...(itemValue.customPropertyId ? {customPropertyId: itemValue.customPropertyId} : {}),
            ...(itemValue.dataGroupId ? {dataGroupId: itemValue.dataGroupId} : {}),
            ...(itemValue.tempDataGroupId ? {tempDataGroupId: itemValue.tempDataGroupId} : {}),
            resourceType: itemValue.resourceType,
            propertyDefaultType: itemValue.propertyDefaultType || itemValue.type,
            propertyName: itemValue.value,
            ...(itemValue.behavior ? {behavior: itemValue.behavior} : {}),
            propertyType: itemValue.type,
            ...(itemValue.joinPropertyType ? {joinPropertyType: itemValue.joinPropertyType} : {}),
            ...(itemValue.propertyObjectKey ? {propertyObjectKey: itemValue.propertyObjectKey} : {}),
            ...(itemValue.scdEvent ? {scdEvent: itemValue.scdEvent} : {}),
          }),
        ];
      }
    }
  }

  if (dataGroupId && isBehaviorShowClause(clause!)) {
    if (
      clause.behavior!.type !== BookmarkTypes.MetricType.Cohort &&
      clause.behavior!.type !== BookmarkTypes.MetricType.People
    ) {
      clause.measurement!.dataGroupId = dataGroupId;
    } else {
      clause.behavior!.dataGroupId = dataGroupId;
    }
  }

  return clause!;
}

const DefaultFormulaNamePrefix = `Formula`;
/**
 * Get the next formula number for naming a new formula metric. To keep it
 * simple we just do n+1 where n is the highest existing formula number.
 *
 * Not trying to get cute where if you have Formula 1 and Formula 3, the next
 * one becomes Formula 2.
 */
export function getNextFormulaName({existingFormulaNames}: {existingFormulaNames: string[]}): string {
  const getFormulaNumber = () => {
    const defaultFormulaNameRegex = RegExp(`^${DefaultFormulaNamePrefix} (\\d+)$`);
    const existingFormulaNumbers = existingFormulaNames
      .map((name) => name.match(defaultFormulaNameRegex)?.[1])
      .filter((numStr) => numStr)
      .map((numStr) => Number(numStr));
    return Math.max(existingFormulaNames.length, ...existingFormulaNumbers) + 1;
  };

  return existingFormulaNames.length === 0
    ? DefaultFormulaNamePrefix
    : `${DefaultFormulaNamePrefix} ${getFormulaNumber()}`;
}

export function getNextFormulaNameFromParams(params: BookmarkTypes.InsightsBookmarkParams): string {
  return getNextFormulaName({
    existingFormulaNames: params.sections.show
      .filter((clause) => clause.type === BookmarkTypes.TopLevelMetricType.Formula && clause.name)
      .map((clause) => clause.name!),
  });
}
